// src/utils/shareUtils.js
import jsPDF from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import axiosInstance from '../services/apiClient';
import { captureRecipeCard } from './captureRecipeCard';


function recipeCardTarget(ref) {
  if (!ref?.current) throw new Error('Recipe card is not ready to export.');
  return ref.current;
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not prepare the export file.'));
    reader.onload = () => {
      const dataUrl = reader.result;
      const separator = typeof dataUrl === 'string' ? dataUrl.indexOf(',') : -1;
      if (separator < 0) {
        reject(new Error('Could not prepare the export file.'));
        return;
      }
      resolve(dataUrl.slice(separator + 1));
    };
    reader.readAsDataURL(blob);
  });
}

async function downloadBlob(blob, filename) {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    const path = `recipe-exports/${Date.now()}-${filename}`;
    const { uri } = await Filesystem.writeFile({
      path,
      data: await blobToBase64(blob),
      directory: Directory.Cache,
      recursive: true,
    });
    await Share.share({
      title: filename,
      files: [uri],
      dialogTitle: 'Save or share recipe card',
    });
    return { delivery: 'share' };
  }

  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Allow the browser to consume the download URL before releasing it.
  setTimeout(() => URL.revokeObjectURL(url), 30000);
  return { delivery: 'download' };
}

function canvasBlob(canvas, type) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Could not encode the recipe image.')),
      type, type === 'image/jpeg' ? 0.98 : undefined);
  });
}

function generateRecipePDF(ref, filename) {
  return captureRecipeCard(recipeCardTarget(ref)).then(({ canvas, width, height }) => {
    const pageWidth = width * 72 / 96;
    const pageHeight = height * 72 / 96;
    const pdf = new jsPDF({
      orientation: width > height ? 'landscape' : 'portrait',
      unit: 'pt',
      format: [pageWidth, pageHeight],
      compress: true,
    });
    pdf.setProperties({ title: 'Recipe Card' });
    pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pageWidth, pageHeight);
    return downloadBlob(pdf.output('blob'), filename);
  });
}

export async function generateBlankPDF(ref) {
  return generateRecipePDF(ref, 'Editable_RecipeCard.pdf');
}

export async function generateBlankPNG(ref) {
  const { canvas } = await captureRecipeCard(recipeCardTarget(ref));
  return downloadBlob(await canvasBlob(canvas, 'image/png'), 'Editable_RecipeCard.png');
}

// Filled and blank exports use the same browser-rendered card capture, keeping
// editor CSS, template background, size and placement consistent across formats.

export async function generateFilledPDF(ref) {
  return generateRecipePDF(ref, 'FilledRecipeCard.pdf');
}

async function generateFilledImage(ref, type, filename) {
  const { canvas } = await captureRecipeCard(recipeCardTarget(ref));
  return downloadBlob(await canvasBlob(canvas, type), filename);
}

export async function generateFilledPNG(ref) {
  return generateFilledImage(ref, 'image/png', 'FilledRecipeCard.png');
}

export async function generateFilledJPG(ref) {
  return generateFilledImage(ref, 'image/jpeg', 'FilledRecipeCard.jpg');
}

/* 📦 Plan PDF Export (Backend-generated PDF) */
export async function generatePlanPDF(planId) {
  try {
    const response = await axiosInstance.post(
      `/api/planning/${planId}/export-pdf`,
      {}, // No body needed for now
      { responseType: 'blob' }
    );

    const blob = response.data;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `MealPlan_${planId}.pdf`;
    link.click();

    console.log(`[shareUtils] Meal plan PDF downloaded for plan ID ${planId}`);
  } catch (err) {
    console.error('[shareUtils] Error generating meal plan PDF', err);
    alert('Failed to generate meal plan PDF. Please try again.');
  }
}
